import { useState, useEffect, useMemo } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, Calendar, Banknote, Eye, Bell, AlertTriangle, TrendingDown, Check, PiggyBank, ArrowUp, ShoppingCart, Users, Wrench, FileDown, Mail } from 'lucide-react';
import { toast } from 'sonner';

const SECRET_CODE = '112118';

const SuiviAnnuel = () => {
  const [bilanData, setBilanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [showPoche, setShowPoche] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState('');
  const [pocheLocal, setPocheLocal] = useState({});
  const [savingPoche, setSavingPoche] = useState(null);
  const [seuilAlerteMarge, setSeuilAlerteMarge] = useState(5);
  const [margeConfort, setMargeConfort] = useState(10);

  const handleCodeChange = (e) => {
    const value = e.target.value;
    setCode(value);
    if (value === SECRET_CODE) {
      setShowPoche(true);
      setShowCodeInput(false);
      setCode('');
      toast.success('Accès autorisé', { duration: 1500 });
    }
  };

  const handleIconClick = () => {
    if (showPoche) {
      setShowPoche(false);
    } else {
      setShowCodeInput(!showCodeInput);
    }
  };

  const fetchBilan = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/bilan-reel/${selectedYear}`);
      setBilanData(response.data);
      const pocheMap = {};
      response.data.months.forEach(m => {
        if (m.sorti > 0) pocheMap[m.mois_num] = m.sorti;
      });
      setPocheLocal(pocheMap);
    } catch (error) {
      console.error('Error fetching bilan:', error);
      toast.error('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBilan(); }, [selectedYear]);

  // Enrich months with pocket money calculation
  const months = useMemo(() => {
    if (!bilanData) return [];
    return bilanData.months.map(m => {
      const sorti = pocheLocal[m.mois_num] || 0;
      // Disponible = max poche en gardant margeConfort% de marge minimum
      // resultat - poche >= margeConfort/100 * ca_ttc
      // poche <= resultat - margeConfort/100 * ca_ttc
      const poche_max = m.ca_ttc > 0 ? Math.max(0, m.resultat - (margeConfort / 100 * m.ca_ttc)) : 0;
      const resultat_apres = m.resultat - sorti;
      const marge_apres = m.ca_ttc > 0 ? (resultat_apres / m.ca_ttc) * 100 : 0;
      return { ...m, sorti, poche_max, resultat_apres, marge_apres };
    });
  }, [bilanData, pocheLocal, margeConfort]);

  const totals = useMemo(() => {
    if (!months.length) return null;
    return months.reduce((acc, m) => ({
      ca_ttc: acc.ca_ttc + m.ca_ttc,
      ca_ht: acc.ca_ht + m.ca_ht,
      ca_traiteur_ht: acc.ca_traiteur_ht + (m.ca_traiteur_ht || 0),
      depenses_chef: acc.depenses_chef + m.depenses_chef,
      charges_mensuelles: acc.charges_mensuelles + m.charges_mensuelles,
      resultat: acc.resultat + m.resultat,
      couverts: acc.couverts + m.couverts,
      poche_max: acc.poche_max + m.poche_max,
      sorti: acc.sorti + m.sorti,
      resultat_apres: acc.resultat_apres + m.resultat_apres,
      achats_total: acc.achats_total + m.achats_total,
      pertes: acc.pertes + m.pertes,
      commandes: acc.commandes + m.commandes,
    }), { ca_ttc: 0, ca_ht: 0, ca_traiteur_ht: 0, depenses_chef: 0, charges_mensuelles: 0, resultat: 0, couverts: 0, poche_max: 0, sorti: 0, resultat_apres: 0, achats_total: 0, pertes: 0, commandes: 0 });
  }, [months]);

  const alerts = useMemo(() => {
    if (!totals) return [];
    const newAlerts = [];
    months.forEach(m => {
      if (m.ca_ttc > 0) {
        if (m.resultat < 0) {
          newAlerts.push({ type: 'danger', mois: m.mois, message: `Résultat négatif : ${formatCurrency(m.resultat)}`, conseil: `Réduire les charges de ${formatCurrency(Math.abs(m.resultat))} minimum` });
        } else if (m.marge_pct < seuilAlerteMarge) {
          newAlerts.push({ type: 'warning', mois: m.mois, message: `Marge faible : ${m.marge_pct.toFixed(1)}%`, conseil: `Objectif: passer au dessus de ${seuilAlerteMarge}%` });
        }
      }
    });
    if (totals.resultat < 0) {
      newAlerts.unshift({ type: 'danger', mois: 'ANNUEL', message: `Résultat annuel négatif : ${formatCurrency(totals.resultat)}`, conseil: 'Revoir les charges ou augmenter le CA' });
    }
    return newAlerts;
  }, [months, totals, seuilAlerteMarge]);

  const sendTelegramAlerts = async () => {
    if (alerts.length === 0) { toast.success('Aucune alerte'); return; }
    let message = `<b>ALERTES BUDGET ${selectedYear}</b>\n\n`;
    alerts.forEach(a => {
      message += `${a.type === 'danger' ? '!!' : '!'} <b>${a.mois}</b>\n${a.message}\n${a.conseil}\n\n`;
    });
    message += `<b>Résumé:</b>\nCA: ${formatCurrency(totals.ca_ttc)}\nRésultat: ${formatCurrency(totals.resultat)}`;
    try {
      await api.post('/telegram/send-alert', null, { params: { message, role: 'patron' } });
      toast.success('Alertes envoyées sur Telegram');
    } catch { toast.error('Erreur envoi Telegram'); }
  };

  const downloadPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const response = await api.get(`/export/bilan-pdf/${selectedYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Bilan_LeMas_${selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur génération PDF'); }
  };

  const emailPDF = async () => {
    try {
      toast.info('Envoi du bilan par email...');
      const response = await api.post(`/export/bilan-pdf-email/${selectedYear}`);
      toast.success(response.data.message);
    } catch { toast.error('Erreur envoi email'); }
  };

  const handleSortiChange = async (moisNum, value) => {
    const montant = parseFloat(value) || 0;
    setPocheLocal(prev => ({ ...prev, [moisNum]: montant }));
    setSavingPoche(moisNum);
    try {
      await api.post('/poche', { annee: selectedYear, mois_num: moisNum, montant_sorti: montant });
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSavingPoche(null); }
  };

  const currentMonth = new Date().getMonth();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <RefreshCw className="w-7 h-7 animate-spin" style={{ color: 'rgb(var(--lm-gold))' }} />
        <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
      </div>
    );
  }

  if (!bilanData || !totals) return null;

  return (
    <div className="space-y-6" data-testid="suivi-annuel">
      {/* Header */}
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Synthese</p>
          <h2 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Calendar style={{ color: 'rgb(var(--lm-gold))' }} className="cursor-pointer hover:opacity-70" onClick={handleIconClick} size={20} data-testid="calendar-icon" />
            Bilan Reel {selectedYear}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-2 py-1 text-sm font-mono" data-testid="year-select">
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
            <option value="2022">2022</option>
          </select>
          <Button onClick={downloadPDF} size="sm" variant="ghost" className="text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]" data-testid="export-pdf-btn">
            <FileDown size={14} className="mr-1" /> PDF
          </Button>
          <Button onClick={emailPDF} size="sm" variant="ghost" className="text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]" data-testid="email-pdf-btn">
            <Mail size={14} className="mr-1" /> Email
          </Button>
          <Button onClick={fetchBilan} size="sm" variant="ghost" className="text-[rgb(var(--lm-text-secondary))]" data-testid="refresh-btn">
            <RefreshCw size={14} />
          </Button>
          {showCodeInput && (
            <input type="password" value={code} onChange={handleCodeChange} placeholder="------" className="w-20 bg-transparent border-b border-[rgb(var(--lm-text-muted))] text-center text-sm font-mono focus:outline-none" style={{ borderColor: 'rgb(var(--lm-gold))' }} autoFocus maxLength={6} data-testid="secret-code-input" />
          )}
          {showPoche && (
            <button onClick={() => setShowPoche(false)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))] p-1" data-testid="hide-poche-btn"><Eye size={16} /></button>
          )}
        </div>
      </div>

      {/* KPIs réels */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-gold) / 0.3)' }}>
          <p className="kpi-label">Charges fixes / mois</p>
          <p className="font-mono text-lg" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(bilanData.charges_fixes_mensuel)}</p>
        </div>
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }}>
          <p className="kpi-label">Salaires charges / mois</p>
          <p className="font-mono text-lg text-blue-400">{formatCurrency(bilanData.salaires_charges_mensuel)}</p>
        </div>
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '180ms', animationFillMode: 'backwards' }}>
          <p className="kpi-label">Depenses chef (annee)</p>
          <p className="font-mono text-lg text-orange-400">{formatCurrency(totals.depenses_chef)}</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">Achats + Pertes + Commandes</p>
        </div>
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }}>
          <p className="kpi-label">Seuil alerte marge</p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <Input type="number" value={seuilAlerteMarge} onChange={(e) => setSeuilAlerteMarge(parseFloat(e.target.value) || 0)} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-center w-16 h-8 text-sm" data-testid="seuil-alerte" />
            <span style={{ color: 'rgb(var(--lm-orange))' }} className="text-sm">%</span>
          </div>
        </div>
      </div>

      {/* Alertes */}
      {alerts.length > 0 && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-red) / 0.3)' }} data-testid="alerts-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle style={{ color: 'rgb(var(--lm-red))' }} size={18} />
              <span className="font-medium text-sm" style={{ color: 'rgb(var(--lm-red))' }}>{alerts.length} Alerte(s)</span>
            </div>
            <Button onClick={sendTelegramAlerts} size="sm" className="bg-[rgb(var(--lm-red))] text-white hover:opacity-90 text-xs" data-testid="send-telegram-btn">
              <Bell size={12} className="mr-1" /> Telegram
            </Button>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {alerts.map((alert, i) => (
              <div key={i} className={`p-2 rounded-sm text-xs ${alert.type === 'danger' ? 'bg-[rgb(var(--lm-red)/0.1)]' : 'bg-[rgb(var(--lm-orange)/0.1)]'}`}>
                <span className="font-medium">{alert.mois}:</span> {alert.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {alerts.length === 0 && totals.ca_ttc > 0 && (
        <div className="lm-card p-4 animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-green) / 0.3)' }}>
          <div className="flex items-center gap-2">
            <Check style={{ color: 'rgb(var(--lm-green))' }} size={18} />
            <span style={{ color: 'rgb(var(--lm-green))' }} className="text-sm">Tout est OK ! Aucune alerte.</span>
          </div>
        </div>
      )}

      {/* Table bilan réel */}
      <div className="lm-card overflow-x-auto animate-slide-up" style={{ animationDelay: '360ms', animationFillMode: 'backwards' }}>
        <table className="w-full text-sm" data-testid="bilan-table">
          <thead>
            <tr className="border-b border-[rgb(var(--lm-border))]">
              <th className="text-left py-3 px-2 text-[rgb(var(--lm-text-secondary))]">Mois</th>
              <th className="text-right py-3 px-2 text-[rgb(var(--lm-text-secondary))]">CA TTC</th>
              <th className="text-right py-3 px-2 text-[rgb(var(--lm-text-secondary))]">CA HT</th>
              <th className="text-right py-3 px-2 text-lemas-gold">Traiteur</th>
              <th className="text-right py-3 px-2 text-orange-400">
                <span className="flex items-center justify-end gap-1"><ShoppingCart size={12} />Chef</span>
              </th>
              <th className="text-right py-3 px-2 text-blue-400">
                <span className="flex items-center justify-end gap-1"><Users size={12} />Salaires</span>
              </th>
              <th className="text-right py-3 px-2 text-[rgb(var(--lm-text-secondary))]">
                <span className="flex items-center justify-end gap-1"><Wrench size={12} />Fixes</span>
              </th>
              <th className="text-right py-3 px-2 font-medium">Résultat</th>
              <th className="text-right py-3 px-2 text-[rgb(var(--lm-text-secondary))]">Marge</th>
              <th className="text-center py-3 px-2 text-[rgb(var(--lm-text-secondary))]">État</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month, index) => {
              const isCurrentMonth = index === currentMonth;
              const hasAlert = month.ca_ttc > 0 && (month.resultat < 0 || month.marge_pct < seuilAlerteMarge);
              const hasChefSpending = month.depenses_chef > 0;
              
              return (
                <tr key={month.mois} className={`border-b border-[rgb(var(--lm-border))] ${isCurrentMonth ? 'bg-lemas-gold/10' : ''} ${month.ca_ttc === 0 ? 'opacity-30' : ''} ${hasAlert ? 'bg-lemas-danger/5' : ''}`}>
                  <td className="py-3 px-2">
                    <span className={`font-medium ${isCurrentMonth ? 'text-lemas-gold' : ''}`}>{month.mois}</span>
                    {isCurrentMonth && <span className="ml-1 text-xs text-lemas-gold">●</span>}
                  </td>
                  <td className="py-3 px-2 text-right font-mono">{formatCurrency(month.ca_ttc)}</td>
                  <td className="py-3 px-2 text-right font-mono text-[rgb(var(--lm-text-secondary))]">{formatCurrency(month.ca_ht)}</td>
                  <td className="py-3 px-2 text-right font-mono text-lemas-gold">{month.ca_traiteur_ht > 0 ? formatCurrency(month.ca_traiteur_ht) : '-'}</td>
                  <td className={`py-3 px-2 text-right font-mono ${hasChefSpending ? 'text-orange-400' : 'text-[rgb(var(--lm-text-muted))]'}`}>
                    {formatCurrency(month.depenses_chef)}
                  </td>
                  <td className="py-3 px-2 text-right font-mono text-blue-400">{formatCurrency(month.salaires_charges)}</td>
                  <td className="py-3 px-2 text-right font-mono text-[rgb(var(--lm-text-secondary))]">{formatCurrency(month.charges_fixes)}</td>
                  <td className={`py-3 px-2 text-right font-mono font-medium ${month.resultat >= 0 ? 'text-lemas-green' : 'text-lemas-danger'}`}>
                    {formatCurrency(month.resultat)}
                  </td>
                  <td className={`py-3 px-2 text-right font-mono ${month.marge_pct >= seuilAlerteMarge ? 'text-lemas-green' : month.marge_pct >= 0 ? 'text-lemas-warning' : 'text-lemas-danger'}`}>
                    {month.ca_ttc > 0 ? `${month.marge_pct.toFixed(1)}%` : '-'}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {month.ca_ttc > 0 && (hasAlert ? <TrendingDown className="text-lemas-danger mx-auto" size={14} /> : <Check className="text-lemas-green mx-auto" size={14} />)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-lemas-gold/30 bg-[rgb(var(--lm-surface))]-elevated/50">
              <td className="py-4 px-2 font-title text-lg">TOTAL</td>
              <td className="py-4 px-2 text-right font-mono text-lg text-lemas-gold">{formatCurrency(totals.ca_ttc)}</td>
              <td className="py-4 px-2 text-right font-mono">{formatCurrency(totals.ca_ht)}</td>
              <td className="py-4 px-2 text-right font-mono text-lemas-gold">{totals.ca_traiteur_ht > 0 ? formatCurrency(totals.ca_traiteur_ht) : '-'}</td>
              <td className="py-4 px-2 text-right font-mono text-orange-400 font-medium">{formatCurrency(totals.depenses_chef)}</td>
              <td className="py-4 px-2 text-right font-mono text-blue-400">{formatCurrency(totals.charges_mensuelles - totals.charges_mensuelles + bilanData.salaires_charges_mensuel * 12)}</td>
              <td className="py-4 px-2 text-right font-mono">{formatCurrency(bilanData.charges_fixes_mensuel * 12)}</td>
              <td className={`py-4 px-2 text-right font-mono text-lg font-medium ${totals.resultat >= 0 ? 'text-lemas-green' : 'text-lemas-danger'}`}>
                {formatCurrency(totals.resultat)}
              </td>
              <td className={`py-4 px-2 text-right font-mono ${totals.ca_ttc > 0 && (totals.resultat / totals.ca_ttc * 100) >= seuilAlerteMarge ? 'text-lemas-green' : 'text-lemas-warning'}`}>
                {totals.ca_ttc > 0 ? ((totals.resultat / totals.ca_ttc) * 100).toFixed(1) : 0}%
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ========== ARGENT DE POCHE - Section cachée ========== */}
      {showPoche && (
        <div className="space-y-4" data-testid="poche-section">
          <div className="lm-card p-5 border-lemas-gold/30 bg-gradient-to-br from-lemas-gold/5 to-transparent">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <PiggyBank className="text-lemas-gold" size={22} />
                <h3 className="font-title text-xl text-lemas-gold">Argent de poche</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Marge confort</span>
                <Input type="number" value={margeConfort} onChange={(e) => setMargeConfort(parseFloat(e.target.value) || 0)} className="bg-lemas-bg border-lemas-gold/20 font-mono text-center w-16 h-7 text-sm" data-testid="marge-confort" />
                <span className="text-lemas-gold text-sm">%</span>
              </div>
            </div>
            <p className="text-xs text-[rgb(var(--lm-text-secondary))] mb-4">
              Basé sur vos <span className="text-[rgb(var(--lm-text))] font-medium">vraies dépenses</span>. Le disponible = ce que vous pouvez prendre en gardant <span className="text-lemas-gold font-medium">{margeConfort}%</span> de marge. Se recalcule au fur et à mesure des factures du chef.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="poche-table">
                <thead>
                  <tr className="border-b border-lemas-gold/20">
                    <th className="text-left py-3 px-2 text-[rgb(var(--lm-text-secondary))]">Mois</th>
                    <th className="text-right py-3 px-2 text-[rgb(var(--lm-text-secondary))]">Résultat réel</th>
                    <th className="text-right py-3 px-2 text-[rgb(var(--lm-text-secondary))]">Marge</th>
                    <th className="text-right py-3 px-2 text-lemas-gold">Disponible</th>
                    <th className="text-right py-3 px-2 text-[rgb(var(--lm-text))]">Sorti</th>
                    <th className="text-right py-3 px-2 text-[rgb(var(--lm-text-secondary))]">Marge après</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((month, index) => {
                    const isCurrentMonth = index === currentMonth;
                    const sortiTrop = month.sorti > month.poche_max && month.ca_ttc > 0;
                    
                    if (month.ca_ttc === 0) return (
                      <tr key={month.mois} className="border-b border-[rgb(var(--lm-border))] opacity-30">
                        <td className="py-3 px-2 font-medium">{month.mois}</td>
                        <td colSpan={5} className="py-3 px-2 text-center text-xs text-[rgb(var(--lm-text-muted))]">Pas de CA</td>
                      </tr>
                    );
                    
                    return (
                      <tr key={month.mois} className={`border-b border-[rgb(var(--lm-border))] ${isCurrentMonth ? 'bg-lemas-gold/10' : ''} ${sortiTrop ? 'bg-red-900/10' : ''}`}>
                        <td className="py-3 px-2">
                          <span className={`font-medium ${isCurrentMonth ? 'text-lemas-gold' : ''}`}>{month.mois}</span>
                        </td>
                        <td className={`py-3 px-2 text-right font-mono ${month.resultat >= 0 ? 'text-lemas-green' : 'text-lemas-danger'}`}>
                          {formatCurrency(month.resultat)}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-[rgb(var(--lm-text-secondary))]">
                          {month.marge_pct.toFixed(1)}%
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-lemas-gold font-medium" data-testid={`poche-dispo-${month.mois_num}`}>
                          {formatCurrency(month.poche_max)}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <Input
                            type="number"
                            value={pocheLocal[month.mois_num] || ''}
                            onChange={(e) => handleSortiChange(month.mois_num, e.target.value)}
                            placeholder="0"
                            className={`bg-transparent border-b ${sortiTrop ? 'border-lemas-danger text-lemas-danger' : 'border-lemas-gold/30 text-[rgb(var(--lm-text))]'} text-right font-mono p-0 h-auto w-20 ml-auto`}
                            data-testid={`poche-sorti-${month.mois_num}`}
                          />
                        </td>
                        <td className={`py-3 px-2 text-right font-mono font-medium ${month.marge_apres >= margeConfort ? 'text-lemas-green' : month.marge_apres >= 0 ? 'text-lemas-warning' : 'text-lemas-danger'}`}>
                          {month.marge_apres.toFixed(1)}%
                          {month.sorti > 0 && (
                            <span className="ml-1">
                              {month.marge_apres >= margeConfort ? <Check size={12} className="inline text-lemas-green" /> : <AlertTriangle size={12} className="inline text-lemas-warning" />}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-lemas-gold/30 bg-[rgb(var(--lm-surface))]-elevated/50">
                    <td className="py-4 px-2 font-title text-lg text-lemas-gold">TOTAL</td>
                    <td className={`py-4 px-2 text-right font-mono font-medium ${totals.resultat >= 0 ? 'text-lemas-green' : 'text-lemas-danger'}`}>
                      {formatCurrency(totals.resultat)}
                    </td>
                    <td className="py-4 px-2 text-right font-mono text-[rgb(var(--lm-text-secondary))]">
                      {totals.ca_ttc > 0 ? ((totals.resultat / totals.ca_ttc) * 100).toFixed(1) : 0}%
                    </td>
                    <td className="py-4 px-2 text-right font-mono text-lemas-gold font-bold text-lg" data-testid="poche-dispo-total">
                      {formatCurrency(totals.poche_max)}
                    </td>
                    <td className="py-4 px-2 text-right font-mono text-[rgb(var(--lm-text))] font-medium" data-testid="poche-sorti-total">
                      {formatCurrency(totals.sorti)}
                    </td>
                    <td className={`py-4 px-2 text-right font-mono font-medium ${totals.ca_ttc > 0 && (totals.resultat_apres / totals.ca_ttc * 100) >= margeConfort ? 'text-lemas-green' : 'text-lemas-warning'}`}>
                      {totals.ca_ttc > 0 ? ((totals.resultat_apres / totals.ca_ttc) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Résumé Poche */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lm-card p-5 text-center border-lemas-gold/40 bg-gradient-to-b from-lemas-gold/10 to-transparent">
              <div className="flex items-center justify-center gap-2 mb-1">
                <PiggyBank className="text-lemas-gold" size={16} />
                <p className="kpi-label">Disponible annuel</p>
              </div>
              <p className="font-mono text-2xl text-lemas-gold" data-testid="poche-annual-dispo">{formatCurrency(totals.poche_max)}</p>
              <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">{formatCurrency(totals.poche_max / 12)} / mois</p>
            </div>
            <div className="lm-card p-5 text-center border-white/20">
              <div className="flex items-center justify-center gap-2 mb-1">
                <ArrowUp className="text-[rgb(var(--lm-text))]" size={16} />
                <p className="kpi-label">Sorti total</p>
              </div>
              <p className="font-mono text-2xl text-[rgb(var(--lm-text))]" data-testid="poche-annual-sorti">{formatCurrency(totals.sorti)}</p>
            </div>
            <div className={`lm-card p-5 text-center ${totals.poche_max - totals.sorti >= 0 ? 'border-lemas-green/30' : 'border-lemas-danger/30'}`}>
              <div className="flex items-center justify-center gap-2 mb-1">
                <Banknote className={totals.poche_max - totals.sorti >= 0 ? 'text-lemas-green' : 'text-lemas-danger'} size={16} />
                <p className="kpi-label">Reste à prendre</p>
              </div>
              <p className={`font-mono text-2xl ${totals.poche_max - totals.sorti >= 0 ? 'text-lemas-green' : 'text-lemas-danger'}`} data-testid="poche-annual-reste">
                {formatCurrency(totals.poche_max - totals.sorti)}
              </p>
            </div>
            <div className={`lm-card p-5 text-center ${totals.resultat_apres >= 0 ? 'border-lemas-green/30' : 'border-lemas-danger/30'}`}>
              <p className="kpi-label">Résultat après poche</p>
              <p className={`font-mono text-2xl ${totals.resultat_apres >= 0 ? 'text-lemas-green' : 'text-lemas-danger'}`}>
                {formatCurrency(totals.resultat_apres)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="lm-card p-5 text-center border-lemas-gold/30">
          <p className="kpi-label">CA Total 2025</p>
          <p className="font-mono text-2xl text-lemas-gold">{formatCurrency(totals.ca_ttc)}</p>
        </div>
        <div className="lm-card p-5 text-center border-orange-400/30">
          <p className="kpi-label">Total Dépenses</p>
          <p className="font-mono text-2xl text-orange-400">{formatCurrency(totals.depenses_chef + totals.charges_mensuelles)}</p>
        </div>
        <div className={`lm-card p-5 text-center ${totals.resultat >= 0 ? 'border-lemas-green/30' : 'border-lemas-danger/30'}`}>
          <p className="kpi-label">Résultat Net</p>
          <p className={`font-mono text-2xl ${totals.resultat >= 0 ? 'text-lemas-green' : 'text-lemas-danger'}`}>{formatCurrency(totals.resultat)}</p>
        </div>
      </div>
    </div>
  );
};

export default SuiviAnnuel;
